import type { PipeTransform } from '@angular/core';
import { Pipe, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { filter } from 'fuzzy';
import { castArray, get, isFunction, isString } from 'lodash';
import type { OnyxPaths } from '../components/table/interfaces';
import { UtilityHelper } from '../helpers';
import type { OnyxMaybeArray } from '../interfaces';

export type OnyxFilterExtract<T> =
  OnyxMaybeArray<OnyxPaths<T> | ((item: T) => string)> | undefined;

@Pipe({
  name: 'onyxFilter',
})
export class OnyxFilterPipe implements PipeTransform {
  private readonly translateService = inject(TranslateService);

  public transform<T>(
    items: T[],
    query: string,
    extract: OnyxFilterExtract<T>,
  ): T[] {
    if (!query) return items;

    query = UtilityHelper.toCompareString(query);

    return filter(query, items, {
      extract: (item) => {
        const value = this.extractValue(item, extract);
        return isString(value) ? this.translateService.instant(value) : value;
      },
    }).map((result) => result.original);
  }

  private extractValue<T>(item: T, extract: OnyxFilterExtract<T>): string {
    return castArray(extract)
      .filter((value) => value != null)
      .map((extractor) => {
        if (isString(extractor)) {
          return get(item, extractor);
        } else if (isFunction(extractor)) {
          return extractor(item);
        }
        return JSON.stringify(item);
      })
      .join('');
  }
}
